import { type MarkRequired } from "ts-essentials";
import { z } from "zod";

import type * as amplitude from "../amplitude";

import { formatting } from "@north-beam/nb-common";

import {
  type LoggerTransport,
  type LoggerLevel,
  LoggerTransportsSchema,
  LoggerLevels,
  LoggerEnvs,
  type LoggerEnv,
} from "../constants";

export type ConfigurationEnvMap<T, E extends readonly string[]> = Partial<
  Record<E[number], T>
>;

export type ConfigurationEnvValue<T, E extends readonly string[]> =
  | T
  | ConfigurationEnvMap<T, E>;

export type LoggerConfiguration<
  E extends readonly string[],
  A extends amplitude.AmplitudeConfig,
> = {
  readonly defaultName: string;
  /**
   * Configuration for the Amplitude client.  This configuration allows the {@link Logger} to
   * make calls to Amplitude's 'logEvent' method, regardless of what specific Amplitude package
   * is being used (e.g. 'ampitude-js' or the react-native Amplitude SDK).
   */
  readonly amplitude: A;
  /**
   * Whether or not events that are logged to Amplitude should also be logged to the browser.  This
   * is useful for debugging/development purposes.
   *
   * Note: This behavior can also be accomplished by dynamically specified/overriden by providing
   * the 'emitToBrowser' option to the {@link Logger}'s {@link Logger.event} method:
   *
   * >>> logger.event.exportSalesChartData("...", {}, { emitToBrowser: true })
   */
  readonly emitEventsToBrowser?: ConfigurationEnvValue<boolean, E>;
  /**
   * Whether or not notifications that are emitted to BugSnag should also be logged to the browser.
   * This is useful for debugging/development purposes.
   */
  readonly emitNotificationsToBrowser?: ConfigurationEnvValue<boolean, E>;
  /**
   * The various mediums (or {@link LoggerTransport}(s)) that the {@link Logger} should use to emit
   * general log, event or notify statements to.  Can be provided as a single array of transports,
   * {@link LoggerTransport[]}, or a mapping of environment values, {@link LoggerEnv}, to an array
   * of transports, {@link LoggerTransport[]}, in the case that the transports or mediums should
   * vary on a per-environment basis.
   */
  readonly transports?: ConfigurationEnvValue<LoggerTransport[], E>;
  /**
   * A string representation of the internal module name that the {@link Logger} is being configured
   * for.
   */
  readonly module?: string;
  readonly prettyLog?: ConfigurationEnvValue<boolean, E>;
  readonly environments: E;
  readonly nodeEnvironment: LoggerEnv;
  /**
   * The current environment that the {@link Logger} is being configured in.
   */
  readonly environment: E[number];
  /**
   * The minimum log level, {@link LoggerLevel}, for general log statements.  This controls the
   * log level threshold for which logs should be emitted if their provided levels exceed.  This
   * configuration value can be provided as either a single {@link LoggerLevel}, or a mapping of
   * environment values, {@link LoggerEnv}, to a {@link LoggerLevel}, in the case that the log
   * levels should vary on a per-environment basis.
   */
  readonly logLevel?: ConfigurationEnvValue<LoggerLevel, E>;
};

export type ParsedLoggerConfiguration<
  E extends readonly string[],
  A extends amplitude.AmplitudeConfig,
> = MarkRequired<LoggerConfiguration<E, A>, "module">;

const BooleanEnvSchema = <E extends readonly string[]>(environments: E) =>
  z
    .union([
      z.boolean(),
      z.object(
        environments.reduce(
          (prev, env) => ({ ...prev, [env]: z.boolean().optional() }),
          {
            development: z.boolean().optional(),
            production: z.boolean().optional(),
            test: z.boolean().optional(),
          },
        ),
      ),
    ])
    .optional();

export const LogConfigurationSchema = <E extends readonly string[]>(
  environments: E,
) =>
  z.object({
    module: z.string().optional(),
    amplitude: z.object({
      apiKey: z.string(),
    }),
    defaultName: z.string().nonempty(),
    nodeEnvironment: LoggerEnvs.schema,
    environment: z.enum([...environments] as E[number][] as [
      E[number],
      ...E[number][],
    ]),
    emitEventsToBrowser: BooleanEnvSchema(environments),
    prettyLog: BooleanEnvSchema(environments),
    emitNotificationsToBrowser: BooleanEnvSchema(environments),
    logLevel: z
      .union([
        LoggerLevels.schema,
        z.object(
          environments.reduce(
            (prev, env) => ({ ...prev, [env]: LoggerLevels.schema.optional() }),
            {},
          ),
        ),
      ])
      .optional(),
    transports: z
      .union([
        LoggerTransportsSchema,
        z.object(
          environments.reduce(
            (prev, env) => ({
              ...prev,
              [env]: LoggerTransportsSchema.optional(),
            }),
            {},
          ),
        ),
      ])
      .optional(),
  });

export const valueIsEnvMap = <T, E extends readonly string[]>(
  config: ConfigurationEnvValue<T, E>,
  environments: E,
): config is ConfigurationEnvMap<T, E> => {
  if (typeof config === "object" && config !== null) {
    const keysPresent = environments.map((e) => e in config);
    return keysPresent.filter(Boolean).length > 0;
  }
  return false;
};

/**
 * Validates and parses the object used to configure the {@link Logger} instance.
 */
export const parseConfiguration = <
  E extends readonly string[],
  A extends amplitude.AmplitudeConfig,
>({
  amplitude: { apiKey: _apiKey, ...amplitude },
  environments,
  ...config
}: LoggerConfiguration<E, A>): ParsedLoggerConfiguration<E, A> => {
  const parsed = LogConfigurationSchema(environments || []).safeParse({
    ...config,
    amplitude: { apiKey: _apiKey },
  });
  if (parsed.success) {
    const {
      module: _module,
      amplitude: { apiKey },
      ...rest
    } = parsed.data;

    const module = _module || process.env.npm_package_name;
    if (module === undefined) {
      throw new TypeError(
        `Detected invalid value '${process.env.npm_package_name}' for npm_package_name.`,
      );
    }

    return {
      environments,
      amplitude: { apiKey, ...amplitude },
      module,
      ...rest,
    } as ParsedLoggerConfiguration<E, A>;
  }
  throw new TypeError(formatting.formatZodError(parsed.error));
};
